import './Question.scoped.scss'

import QuestionPhonePreview from 'Shared/components/QuestionPhonePreview'
import InstructionsTextarea from './InstructionsTextarea'
import Answer from './Answer'
import { useStore } from 'react-redux'
import PillEditor from 'Shared/components/PillEditor/PillEditor'
import NextAction from './NextAction'
import Button from 'Shared/components/Button'
import parsePhoneNumber from 'libphonenumber-js/max'
import { useCallback } from 'react'
import useAct from '../hooks/useAct'
import Checkbox from 'Shared/components/Checkbox'
import phone from 'phone'
import PhoneInput from 'react-phone-input-international'

const ensureNumber = (n) => {
  const val = +n
  return isNaN(val) ? 0 : val
}

const labelMappings = {
  intro: 'Introduction',
  instruction: 'Instruction',
  multiple_choice: 'Select one',
  multiple_select: 'Select multiple',
  send_sms: 'Send SMS - Instruction',
  text: 'Question',
  score: 'Ranking',
  transfer: 'Transfer call - Instruction',
  checkbox: 'Checkbox label',
}

const BRANCHING_QUESTION_TYPES = ['multiple_choice']
const GO_TO_NEXT_QUESTION_TYPES = [
  'multiple_select',
  'score',
  'instruction',
  'text',
  'send_sms',
  'transfer',
]

export default function Question({
  id,
  campaignType,
  effortId,
  effortName,
  goals,
  mergeTags,
  isCampaignControlled,
  enableOptOut,
  blastKind,
}) {
  const act = useAct()
  const store = useStore()
  const questions = useSel((s) => _.values(s.questions.entities).filter((q) => q.active))
  const {
    question,
    instructions,
    questionType,
    digitOrder,
    settings = {},
    action,
    nextQuestionId,
  } = useSel((s) => s.questions.entities[id])
  const { textMessageBody, toPhoneNumber } = settings || {}
  const answers = useSel((s) =>
    _.orderBy(
      _.values(s.answers.entities).filter((a) => a.questionId === id && a.active),
      'digitOrder'
    )
  )

  const answerIds = answers.map((a) => a.id)
  const label = labelMappings[questionType]
  const hasAnswers = !_.includes(
    ['instruction', 'intro', 'send_sms', 'transfer', 'score', 'checkbox'],
    questionType
  )
  const includeOptOut = campaignType === 'sms_survey' && enableOptOut && digitOrder === 1
  const isWhatsappFirstMessage =
    campaignType === 'sms_survey' && blastKind === 'whatsapp' && digitOrder === 1
  const canMove = questionType !== 'intro' && !isWhatsappFirstMessage
  const readOnly = isWhatsappFirstMessage
  const moveableOffset = campaignType === 'phone' ? 1 : 0
  const canMoveUp =
    digitOrder > 1 + moveableOffset && !(blastKind === 'whatsapp' && digitOrder === 2)
  const canMoveDown = digitOrder < questions.length

  function move(down) {
    const newOrder = down ? digitOrder + 1 : digitOrder - 1
    const state = store.getState()
    const answers = _.values(state.answers.entities).filter((a) => a.active)

    let affectedIds = _.orderBy(
      questions.filter((e) => e.id !== id),
      'digitOrder'
    ).map((e) => e.id)
    affectedIds.splice(newOrder, 0, id)

    // Apply update to copy of questions to check for cycles
    const localQuestions = questions.map((q, i) => {
      const newDigitOrder = affectedIds.indexOf(q.id) + 1
      return { ...q, digitOrder: newDigitOrder }
    })

    act.scriptBuilder.questions.moveDigitOrder(id, newOrder - 1, { active: true })
  }

  const saveSettings = (settings) =>
    act.scriptBuilder.questions.upsertSettings(id, settings)

  const handlePhoneNumberChange = useCallback((value, countryData) => {
    if (!value) {
      saveSettings({ toPhoneNumber: '' })
      return
    }

    const normalizedValue = String(value).replace('+440', '+44')

    if (normalizedValue.startsWith('+')) {
      const parsed = parsePhoneNumber(normalizedValue)
      if (parsed && parsed.isValid()) {
        saveSettings({ toPhoneNumber: parsed.number })
        return
      }
    }

    const formatted = phone(value, { 
      country: countryData?.countryCode?.toUpperCase() || 'GB' 
    })

    saveSettings({
      toPhoneNumber: formatted.isValid ? formatted.phoneNumber : value
    })
  }, [id])

  return (
    <div className="question-block row">
      <div className="column remaining">
        <div className={`question question-${questionType}`}>
          <div className="attributes">
            <label>{label}</label>
            <div
              className={`content-editor condensed ${hasAnswers ? 'transparent' : 'bordered'}`}
            >
              {readOnly ? (
                <input type="text" value={question} disabled className="full-width" />
              ) : (
                <PillEditor
                  condensed={true}
                  initialValue={question}
                  onChange={useCallback(
                    (body) => act.scriptBuilder.questions.upsert(id, { question: body }),
                    [id]
                  )}
                  availableTags={mergeTags}
                />
              )}
            </div>
          </div>

          {questionType === 'text' && (
            <div className="indent">
              <p>This is an open question where any answer can be entered.</p>
            </div>
          )}

          {campaignType !== 'sms_survey' &&
            ['multiple_choice', 'multiple_select', 'text', 'score'].includes(
              questionType
            ) && (
              <div className="attributes">
                <label></label>
                <Checkbox
                  label="Require an answer to be given"
                  checked={settings.required}
                  onChange={(e) => saveSettings({ required: e.target.checked })}
                />
              </div>
            )}

          {questionType === 'send_sms' && (
            <div className="attributes">
              <label>Text message content</label>
              <div className={`content-editor condensed bordered`}>
                <PillEditor
                  condensed={true}
                  initialValue={textMessageBody}
                  onChange={(textMessageBody) =>
                    act.scriptBuilder.questions.upsertSettings(id, { textMessageBody })
                  }
                  availableTags={mergeTags}
                />
              </div>
            </div>
          )}

          {questionType === 'transfer' && (
            <div className="attributes">
              <label>Phone number to transfer to</label>
              <div className="content-editor condensed bordered">
                <PhoneInput
                  country='gb'
                  preferredCountries={['gb']}
                  value={toPhoneNumber || ''}
                  onChange={handlePhoneNumberChange}
                  placeholder="Enter phone number"
                  autoFormat={false}
                  disableCountryCode
                />
              </div>
            </div>
          )}

          {hasAnswers && questionType !== 'text' && (
            <div className="answers">
              {answerIds.map((id) => (
                <Answer
                  id={id}
                  key={id}
                  showAdvanced={BRANCHING_QUESTION_TYPES.includes(questionType)}
                  effortId={effortId}
                  effortName={effortName}
                  goals={goals}
                  readOnly={readOnly}
                  mergeTags={mergeTags}
                />
              ))}
              {!readOnly && (
                <a
                  className="button naked add-answer"
                  onClick={() =>
                    act.scriptBuilder.answers.add({
                      questionId: id,
                      digitOrder: answers.length,
                      action: 'next_question',
                    })
                  }
                >
                  Add an answer
                </a>
              )}
            </div>
          )}

          {((!settings.required || GO_TO_NEXT_QUESTION_TYPES.includes(questionType)) && !(campaignType === 'sms_survey' && questionType === 'multiple_choice')) && (
            <div className="attributes">
              <label>
                After this {questionType === 'instruction' ? 'instruction' : 'question'}
              </label>
              <NextAction
                questionId={id}
                action={action}
                nextQuestionId={nextQuestionId}
                onChange={(option) =>
                  act.scriptBuilder.questions.upsert(id, option.value)
                }
              />
            </div>
          )}

          {questionType === 'score' && (
            <>
              <div className="attributes">
                <label>Min</label>
                <input
                  name="min"
                  type="text"
                  size="3"
                  placeholder="0"
                  value={settings.min ?? ''}
                  onChange={(e) => saveSettings({ min: ensureNumber(e.target.value) })}
                />
              </div>
              <div className="attributes">
                <label>Max</label>
                <input
                  name="max"
                  type="text"
                  size="3"
                  placeholder="10"
                  value={settings.max ?? ''}
                  onChange={(e) => saveSettings({ max: ensureNumber(e.target.value) })}
                />
              </div>
              <div className="attributes">
                <label>Default</label>
                <input
                  name="default"
                  type="text"
                  size="3"
                  value={settings.default}
                  onChange={(e) => saveSettings({ default: ensureNumber(e.target.value) })}
                />
              </div>
              <div className="attributes">
                <label>Low label</label>
                <input
                  name="score_label_left"
                  type="text"
                  size="12"
                  placeholder="e.g. Bad"
                  value={settings.scoreLabelLeft || ''}
                  onChange={(e) => saveSettings({ scoreLabelLeft: e.target.value })}
                />
              </div>
              <div className="attributes">
                <label>High label</label>
                <input
                  name="score_label_right"
                  type="text"
                  size="12"
                  placeholder="e.g. Good"
                  value={settings.scoreLabelRight || ''}
                  onChange={(e) => saveSettings({ scoreLabelRight: e.target.value })}
                />
              </div>
              <p className="bold smaller dark-grey margin-left">These labels are optional and will appear above the score buttons</p>
            </>
          )}

          <div className="divider"></div>

          {questionType !== 'intro' && !isCampaignControlled && (
            <>
              <div className="attributes flex-start">
                <InstructionsTextarea
                  id={id}
                  initialInstructions={instructions}
                  act={act}
                />
              </div>
              <div className="divider"></div>
            </>
          )}

          <div className="actions">
            {canMove && (
              <>
                {canMoveUp && (
                  <Button
                    smallest
                    secondary
                    extraClass="small-square-button margin-right move-up"
                    onClick={() => move(false)}
                  >
                    <SvgIconsUp />
                  </Button>
                )}
                {canMoveDown && (
                  <Button
                    smallest
                    secondary
                    extraClass="small-square-button margin-right move-down"
                    onClick={() => move(true)}
                  >
                    <SvgIconsDown />
                  </Button>
                )}
              </>
            )}

            <div className="divider vertical"></div>

            {!readOnly && (
              <a
                className="delete margin-bottom half"
                onClick={() => act.scriptBuilder.questions.markInactive(id)}
              >
                <SvgIconsDelete />
              </a>
            )}
          </div>
        </div>
      </div>
      {(campaignType === 'sms_survey' || questionType === 'send_sms') && (
        <div className="column quarter">
          <QuestionPhonePreview
            includeOptOut={includeOptOut}
            questionId={id}
            showCharacterCount={blastKind === 'sms'}
          />
        </div>
      )}
    </div>
  )
}
